import { useState } from 'react'
import { Head } from '@inertiajs/react'
import type { Keg } from '@/types'

type OrganizationContact = {
  name: string
  contact_email: string | null
  contact_phone: string | null
}

type Labels = {
  page_title: string
  owned_by: string
  hero_note: string
  identifier_label: string
  finder_title: string
  finder_body: string
  message_label: string
  message_placeholder: string
  found_cta: string
  found_confirmation: string
  geolocation_notice: string
  member_title: string
  member_body: string
  member_cta: string
  contact_title: string
  contact_intro: string
  contact_cta: string
  contact_reveal_note: string
  no_contact: string
  email_label: string
  phone_label: string
  lead_title: string
  lead_body: string
  lead_cta: string
  powered_by: string
  report_error: string
}

type Props = {
  keg: Keg
  organization: OrganizationContact
  // Owner CTA target. Host-aware server-side: on the QR domain it bridges to the brand/app
  // domain (where the member logs in and operates); on the brand domain it is the login path.
  manage_url: string
  labels: Labels
}

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? ''
}

const stroke = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
}
const KegGlyph = ({ className = 'h-6 w-6' }: { className?: string }) => (
  <svg viewBox="0 0 24 24" {...stroke} className={className} aria-hidden>
    <path d="M8.5 3h7l1 3.2c.75 2.2.75 9.4 0 11.6L15.5 21h-7l-1-3.2c-.75-2.2-.75-9.4 0-11.6L8.5 3Z" />
    <path d="M7.6 8.4h8.8M7.6 15.6h8.8" />
  </svg>
)
const PinIcon = ({ className = 'h-5 w-5' }: { className?: string }) => (
  <svg viewBox="0 0 24 24" {...stroke} className={className} aria-hidden>
    <path d="M12 21s-6-5.2-6-10a6 6 0 1 1 12 0c0 4.8-6 10-6 10Z" />
    <circle cx="12" cy="11" r="2" />
  </svg>
)
const LockIcon = ({ className = 'h-5 w-5' }: { className?: string }) => (
  <svg viewBox="0 0 24 24" {...stroke} className={className} aria-hidden>
    <rect x="5" y="10.5" width="14" height="9.5" rx="2" />
    <path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" />
  </svg>
)

export default function PublicKegsAnonymous({ keg, organization, manage_url, labels }: Props) {
  const [message, setMessage] = useState('')
  const [reported, setReported] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [failed, setFailed] = useState(false)
  const [contactRevealed, setContactRevealed] = useState(false)
  const canSubmit = message.trim().length > 0 && !submitting

  // Optimistic on purpose: the details appear before the request is even sent, and a network failure
  // is swallowed. This block sits on the path that brings a keg home — it must never wait on, or be
  // blocked by, a round-trip. We accept losing the event rather than losing the contact. The state
  // flag also guarantees a single POST per page load.
  //
  // keepalive lets the request survive the immediate navigation of a finder who taps `tel:` a second
  // later — otherwise we would lose exactly the most qualified reveals.
  const revealContact = () => {
    if (contactRevealed) return
    setContactRevealed(true)

    fetch(`/f/${keg.token}/contact_reveals`, {
      method: 'POST',
      headers: { 'X-CSRF-Token': csrfToken() },
      keepalive: true,
    }).catch(() => {})
  }

  // The ONLY trigger for notifying the brewery: an explicit report carrying the finder's message.
  // On success the server has sent the email; we then ask the browser for location in the same
  // gesture (best-effort enrichment). We confirm only once the report actually landed.
  const reportFound = async () => {
    if (!canSubmit) return
    setSubmitting(true)
    setFailed(false)
    try {
      const res = await fetch(`/f/${keg.token}/scans`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
        body: JSON.stringify({ message }),
      })
      if (!res.ok) {
        setFailed(true)
        setSubmitting(false)
        return
      }
      setReported(true)
      const { scan_id } = (await res.json()) as { scan_id: number }
      if (!('geolocation' in navigator)) return
      navigator.geolocation.getCurrentPosition(
        (position) => {
          fetch(`/f/${keg.token}/scans/${scan_id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
            body: JSON.stringify({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            }),
          })
        },
        () => {},
      )
    } catch {
      setFailed(true)
      setSubmitting(false)
    }
  }

  const hasContact = Boolean(organization.contact_email || organization.contact_phone)

  return (
    <>
      <Head title={labels.page_title}>
        <meta name="robots" content="noindex" />
      </Head>
      <div className="min-h-screen bg-paper font-body text-ink">
        <main className="mx-auto w-full max-w-md px-6 py-10">
          {/* HERO — typographic, flat on paper, with a filigree keg watermark. No box. */}
          <section className="animate-fade-in-up relative">
            <KegGlyph className="pointer-events-none absolute -right-4 -top-4 h-40 w-40 rotate-12 text-ink/[0.05]" />
            <h1 className="font-display text-4xl leading-[1.05] text-ink">{labels.owned_by}</h1>
            <p className="mt-1 -rotate-1 font-script text-2xl text-flame">{labels.hero_note}</p>
            <div className="mt-5 inline-flex items-center gap-2 rounded-md border-[1.5px] border-ink px-2.5 py-1">
              <span className="text-[0.7rem] font-semibold uppercase tracking-wide text-ink-faint">
                {labels.identifier_label}
              </span>
              <span className="font-display text-lg leading-none text-ink">{keg.identifier}</span>
            </div>
          </section>

          {/* MEMBER — the staff entrance, placed high for visibility as a distinct tinted panel
              (a background level, not a heavy card) with a clear button. */}
          <a
            href={manage_url}
            className="group mt-8 block rounded-xl bg-paper-2 px-5 py-4 transition-transform hover:-translate-y-0.5 motion-reduce:transform-none"
          >
            <div className="flex items-center gap-2 text-leaf-deep">
              <LockIcon className="h-5 w-5" />
              <span className="font-display text-xl leading-tight text-ink">{labels.member_title}</span>
            </div>
            <p className="mt-1 text-sm text-ink-soft">{labels.member_body}</p>
            <span className="mt-3 inline-flex items-center gap-1.5 rounded-lg border-[1.5px] border-ink px-4 py-2 text-sm font-bold text-ink transition-colors group-hover:bg-ink group-hover:text-paper">
              {labels.member_cta} →
            </span>
          </a>

          {/* FINDER — the primary path. A message is required (the finder's words + recontact
              channel). Flat: heading, field, button — grouped by rhythm, not a card. */}
          <section className="mt-10 border-t border-line pt-8">
            <div className="flex items-center gap-2 text-leaf-deep">
              <PinIcon className="h-5 w-5" />
              <h2 className="font-display text-2xl text-ink">{labels.finder_title}</h2>
            </div>

            {reported ? (
              <div className="mt-3 flex items-start gap-2.5 rounded-lg bg-leaf-tint px-4 py-3.5 text-sm font-medium text-leaf-deep">
                <KegGlyph className="mt-0.5 h-5 w-5 shrink-0" />
                <span>{labels.found_confirmation}</span>
              </div>
            ) : (
              <>
                <p className="mt-1.5 text-sm leading-relaxed text-ink-soft">{labels.finder_body}</p>
                <label htmlFor="finder-message" className="mt-5 block text-xs font-bold uppercase tracking-wide text-ink-faint">
                  {labels.message_label}
                </label>
                <textarea
                  id="finder-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={3}
                  placeholder={labels.message_placeholder}
                  className="mt-1.5 w-full resize-none rounded-lg border-[1.5px] border-line bg-card px-3.5 py-2.5 text-sm text-ink placeholder:text-ink-faint focus:border-ink focus:outline-none"
                />
                <button
                  type="button"
                  onClick={reportFound}
                  disabled={!canSubmit}
                  className="mt-3 w-full rounded-lg bg-leaf px-4 py-3 font-display text-lg text-paper transition-[background-color,transform] hover:bg-leaf-deep disabled:cursor-not-allowed disabled:bg-ink/20 disabled:text-ink/40"
                >
                  {labels.found_cta}
                </button>
                {failed && (
                  <p role="alert" className="mt-2.5 text-sm font-medium text-flame-deep">
                    {labels.report_error}
                  </p>
                )}
                <p className="mt-2.5 text-xs leading-relaxed text-ink-faint">{labels.geolocation_notice}</p>
              </>
            )}
          </section>

          {/* CONTACT — the finder's alternative. Flat, hairline-separated. */}
          <section className="mt-8 border-t border-line pt-6">
            {hasContact ? (
              <>
                <h3 className="text-xs font-bold uppercase tracking-wide text-ink-faint">
                  {labels.contact_title}
                </h3>
                {contactRevealed ? (
                  <div className="mt-3 space-y-2.5">
                    {organization.contact_email && (
                      <a
                        href={`mailto:${organization.contact_email}`}
                        className="flex items-baseline justify-between gap-3 text-sm"
                      >
                        <span className="text-ink-soft">{labels.email_label}</span>
                        <span className="font-semibold text-leaf-deep">{organization.contact_email}</span>
                      </a>
                    )}
                    {organization.contact_phone && (
                      <a
                        href={`tel:${organization.contact_phone}`}
                        className="flex items-baseline justify-between gap-3 text-sm"
                      >
                        <span className="text-ink-soft">{labels.phone_label}</span>
                        <span className="font-semibold text-leaf-deep">{organization.contact_phone}</span>
                      </a>
                    )}
                  </div>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={revealContact}
                      className="mt-3 w-full rounded-lg border border-line bg-card px-4 py-3 text-sm font-semibold text-leaf-deep transition-colors hover:bg-paper-2"
                    >
                      {labels.contact_cta}
                    </button>
                    <p className="mt-2.5 text-xs leading-relaxed text-ink-faint">
                      {labels.contact_reveal_note}
                    </p>
                  </>
                )}
              </>
            ) : (
              <p className="text-sm text-ink-soft">{labels.no_contact}</p>
            )}
          </section>

          {/* LEAD — the single deliberate accent among flat sections: a drenched brand moment. */}
          <section className="relative mt-12 overflow-hidden rounded-2xl bg-leaf-deep px-6 py-8 text-paper">
            <KegGlyph className="pointer-events-none absolute -bottom-8 -right-5 h-40 w-40 -rotate-12 text-paper/[0.06]" />
            <h2 className="font-display text-2xl leading-snug">{labels.lead_title}</h2>
            <p className="mt-1.5 text-sm leading-relaxed text-paper/80">{labels.lead_body}</p>
            <a
              href="/"
              className="mt-4 inline-flex items-center gap-2 rounded-lg bg-mustard px-4 py-2.5 font-display text-base text-ink transition-transform hover:-translate-y-0.5 motion-reduce:transform-none"
            >
              {labels.lead_cta} →
            </a>
          </section>

          <p className="mt-6 text-center text-xs text-ink-faint">{labels.powered_by}</p>
        </main>
      </div>
    </>
  )
}
